import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { cn } from '../../lib/utils';

// Predefined responses for common questions
const RESPONSES = {
  greeting: {
    patterns: ['hola', 'hello', 'buenos días', 'buenas tardes', 'hi'],
    response: '¡Hola! Soy el asistente virtual de Arreglatelo. ¿En qué puedo ayudarte hoy?\n\nPuedo ayudarte con:\n• Crear una solicitud de garantía\n• Consultar el estado de tu garantía\n• Solicitar una factura\n• Información sobre tiempos de reparación'
  },
  warranty_create: {
    patterns: ['crear garantía', 'nueva garantía', 'solicitar garantía', 'abrir garantía', 'rma'],
    response: 'Para crear una nueva solicitud de garantía:\n\n1. Ve a "Nueva Solicitud de Garantía" en tu panel\n2. Rellena tus datos personales\n3. Indica los datos del producto\n4. Describe el problema\n5. Envía la solicitud\n\nRecibirás un email con una etiqueta de Correos gratuita para enviar el producto.\n\n¿Quieres que te lleve al formulario?',
    action: 'navigate_warranty'
  },
  warranty_status: {
    patterns: ['estado garantía', 'consultar garantía', 'ver garantía', 'tracking', 'seguimiento'],
    response: 'Para consultar el estado de tu garantía:\n\n1. Ve a "Consultar Garantías" en tu panel\n2. Selecciona la garantía que quieres revisar\n3. Verás el estado actual y el historial\n\nLos estados posibles son:\n• Solicitud Abierta\n• En Tránsito\n• En Reparación\n• Enviado al Cliente\n• RMA Completado\n\n¿Quieres ver tus garantías?',
    action: 'navigate_warranty_list'
  },
  invoice: {
    patterns: ['factura', 'solicitar factura', 'pedir factura', 'invoice'],
    response: 'Para solicitar una factura:\n\n1. Ve a "Solicitar Factura" en tu panel\n2. Introduce tu NIF/CIF/DNI\n3. Rellena los datos del producto\n4. Envía la solicitud\n\nRecibirás la factura por email una vez procesada.\n\n¿Quieres solicitar una factura ahora?',
    action: 'navigate_invoice'
  },
  time: {
    patterns: ['tiempo', 'cuánto tarda', 'demora', 'días', 'plazo'],
    response: 'Los tiempos estimados son:\n\n• Envío al servicio técnico: 2-5 días laborables\n• Diagnóstico: 2-3 días laborables\n• Reparación: 5-15 días laborables\n• Devolución: 2-5 días laborables\n\n**Tiempo total estimado: 7-30 días laborables**\n\nEstos tiempos pueden variar según la complejidad de la reparación y la disponibilidad de piezas.'
  },
  shipping: {
    patterns: ['envío', 'correos', 'etiqueta', 'enviar', 'paquete'],
    response: 'El proceso de envío es sencillo:\n\n1. Al crear tu solicitud, recibirás una etiqueta de Correos gratuita\n2. Imprime la etiqueta y pégala en el paquete\n3. Lleva el paquete a cualquier oficina de Correos\n4. Guarda el resguardo de envío\n\n**El envío de ida es GRATUITO**. También te devolvemos el producto reparado sin coste.'
  },
  contact: {
    patterns: ['contacto', 'teléfono', 'email', 'hablar', 'llamar'],
    response: 'Puedes contactarnos de varias formas:\n\n• Email: soporte@arreglatelo.es\n• Teléfono: +34 XXX XXX XXX\n• Este chat de asistencia\n\nHorario de atención: L-V 9:00-18:00\n\nSi tienes una urgencia, indica "urgente" en el asunto del email.'
  },
  default: {
    response: 'No estoy seguro de entender tu pregunta. ¿Podrías reformularla?\n\nPuedo ayudarte con:\n• Crear solicitudes de garantía\n• Consultar estados\n• Solicitar facturas\n• Tiempos de reparación\n• Información de envío'
  }
};

const findResponse = (input) => {
  const normalizedInput = input.toLowerCase().trim();
  
  for (const [key, data] of Object.entries(RESPONSES)) {
    if (key === 'default') continue;
    if (data.patterns?.some(pattern => normalizedInput.includes(pattern))) {
      return data;
    }
  }
  
  return RESPONSES.default;
};

/**
 * Virtual assistant chatbot component
 */
export const VirtualAssistant = ({ className, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate typing delay
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));

    const response = findResponse(userMessage.content);
    
    const botMessage = {
      id: Date.now() + 1,
      type: 'bot',
      content: response.response,
      action: response.action,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);
    setIsTyping(false);
  };

  const handleAction = (action) => {
    switch (action) {
      case 'navigate_warranty':
        onNavigate?.('/warranty/new');
        setIsOpen(false);
        break;
      case 'navigate_warranty_list':
        onNavigate?.('/warranty');
        setIsOpen(false);
        break;
      case 'navigate_invoice':
        onNavigate?.('/invoice/new');
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleQuickAction = (action) => {
    const quickMessages = {
      warranty: '¿Cómo creo una solicitud de garantía?',
      status: '¿Cómo consulto el estado de mi garantía?',
      invoice: '¿Cómo solicito una factura?',
      time: '¿Cuánto tarda la reparación?'
    };

    setInput(quickMessages[action] || '');
    handleSend();
  };

  return (
    <div className={cn('fixed bottom-20 right-4 z-[9999]', className)}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="absolute bottom-16 right-0 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-[#E5E7EB] dark:border-gray-700 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#0047FF] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Asistente Virtual</h3>
                  <p className="text-xs text-white/80">Siempre disponible</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Cerrar chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    'flex gap-2',
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {message.type === 'bot' && (
                    <div className="w-8 h-8 bg-[#0047FF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-[#0047FF]" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[80%] p-3 rounded-2xl text-sm',
                      message.type === 'user'
                        ? 'bg-[#0047FF] text-white rounded-tr-sm'
                        : 'bg-[#F5F5F7] dark:bg-gray-700 text-[#0A0A0A] dark:text-white rounded-tl-sm'
                    )}
                  >
                    <p className="whitespace-pre-line">{message.content}</p>
                    {message.action && (
                      <Button
                        size="sm"
                        className="mt-2 w-full bg-[#0047FF] hover:bg-[#003BCC]"
                        onClick={() => handleAction(message.action)}
                      >
                        Ir ahora
                      </Button>
                    )}
                  </div>
                  {message.type === 'user' && (
                    <div className="w-8 h-8 bg-[#0047FF] rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </motion.div>
              ))}
              
              {isTyping && (
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-[#0047FF]/10 rounded-full flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#0047FF]" />
                  </div>
                  <div className="bg-[#F5F5F7] dark:bg-gray-700 p-3 rounded-2xl rounded-tl-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#A1A1AA] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#A1A1AA] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#A1A1AA] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick actions */}
            {messages.length <= 2 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setInput('¿Cómo creo una garantía?');
                    setTimeout(handleSend, 100);
                  }}
                  className="text-xs bg-[#0047FF]/10 text-[#0047FF] px-3 py-1 rounded-full hover:bg-[#0047FF]/20 transition-colors"
                >
                  Nueva garantía
                </button>
                <button
                  onClick={() => {
                    setInput('¿Cuánto tarda la reparación?');
                    setTimeout(handleSend, 100);
                  }}
                  className="text-xs bg-[#0047FF]/10 text-[#0047FF] px-3 py-1 rounded-full hover:bg-[#0047FF]/20 transition-colors"
                >
                  Tiempos
                </button>
                <button
                  onClick={() => {
                    setInput('¿Cómo solicito factura?');
                    setTimeout(handleSend, 100);
                  }}
                  className="text-xs bg-[#0047FF]/10 text-[#0047FF] px-3 py-1 rounded-full hover:bg-[#0047FF]/20 transition-colors"
                >
                  Factura
                </button>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-[#E5E7EB] dark:border-gray-700">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="flex gap-2"
              >
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu pregunta..."
                  className="flex-1"
                  disabled={isTyping}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isTyping}
                  className="bg-[#0047FF] hover:bg-[#003BCC]"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors',
          isOpen ? 'bg-[#52525B]' : 'bg-[#0047FF] hover:bg-[#003BCC]'
        )}
        aria-label={isOpen ? 'Cerrar asistente' : 'Abrir asistente'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </motion.button>
    </div>
  );
};

export default VirtualAssistant;
